import { DictionaryInterface } from 'titus-ts/dist/js/Common/Util/DictionaryInterface';
import BuiltInProperties from 'titus-ts/dist/js/Document/BuiltInProperties';
import { AddinApi } from './AddinApi';
import { defaultHeaderFooterOptions, HeaderFooterOptions } from './HeaderFooterOptions';

export class PowerPointApi extends AddinApi {
    readonly headerShapeName = 'TitusHeader';
    readonly footerShapeName = 'TitusFooter';
    readonly headerFooterShapeHeight = 70;

    public getBodyAsTextAsync = (): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            PowerPoint.run(async (context) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const data: string[] = [];
                const slides = context.presentation.slides;
                context.load(slides);
                await context.sync();

                if (!slides.items) reject('Presentation items can not be null or undefined');
                else {
                    for (const slide of slides.items) {
                        const shapes = slide.shapes;
                        shapes.load('items/textFrame/textRange/text');
                        await context.sync();

                        for (const shape of shapes.items) {
                            data.push(shape.textFrame.textRange.text);
                        }
                    }

                    resolve(data.join(' '));
                }
            });
        });
    };

    public getCustomProperties = (
        requestedCustomPropertiesNames: string[],
    ): Promise<DictionaryInterface<string | string[]>> => {
        return new Promise<DictionaryInterface<string | string[]>>((resolve, reject) => {
            PowerPoint.run(async (context) => {
                const customProperties = context.presentation.properties.customProperties;
                const selectedCustomProperties: DictionaryInterface<string | string[]> = {};
                context.load(customProperties);
                await context.sync();
                if (!customProperties.items) reject('PowerPoint get Custom Properties failed');
                else {
                    customProperties.items.forEach((item) => {
                        if (requestedCustomPropertiesNames.includes(item.key)) {
                            selectedCustomProperties[item.key] = (item.value as string).split(';');
                        }
                    });
                }
                resolve(selectedCustomProperties);
            });
        });
    };

    public getAllCustomProperties = (): Promise<DictionaryInterface<string | string[]>> => {
        return new Promise<DictionaryInterface<string | string[]>>((resolve, reject) => {
            PowerPoint.run(async (context) => {
                const customProperties = context.presentation.properties.customProperties;
                const selectedCustomProperties: DictionaryInterface<string | string[]> = {};
                context.load(customProperties);
                await context.sync();
                if (!customProperties.items) reject('PowerPoint get Custom Properties failed');
                else {
                    const allProps: DictionaryInterface<string | string[]> = Object.fromEntries(
                        Array.from(customProperties.items, ({ key, value }) => [key, value.toString()]),
                    );

                    resolve(allProps);
                }
                resolve(selectedCustomProperties);
            });
        });
    };

    public setCustomProperties(
        selectedCustomProperties: DictionaryInterface<string | string[]>,
        associatedCustomPropertiesNames: string[],
    ): Promise<void> {
        return new Promise<void>((resolve) => {
            PowerPoint.run(async (context) => {
                const fields = Object.getOwnPropertyNames(selectedCustomProperties);
                const customProperties = context.presentation.properties.customProperties;
                context.load(customProperties);
                await context.sync();
                for (let i = 0; i < associatedCustomPropertiesNames.length; i++) {
                    customProperties.getItemOrNullObject(associatedCustomPropertiesNames[i])?.delete();
                }
                await context.sync();
                for (let i = 0; i < fields.length; i++) {
                    const value = selectedCustomProperties[fields[i]];
                    const valueString = typeof value == 'string' ? value : value.join(';');
                    context.presentation.properties.customProperties.add(fields[i], valueString);
                }
                await context.sync();
                resolve();
            });
        });
    }

    public getProperties = (): Promise<BuiltInProperties> => {
        return new Promise<BuiltInProperties>((resolve, reject) => {
            if (!Office.context.requirements.isSetSupported('PowerPointApi', '1.7')) {
                resolve(new BuiltInProperties());
                return;
            }

            PowerPoint.run(async (context) => {
                const properties = context.presentation.properties;
                context.load(properties);
                await context.sync();

                if (!properties) {
                    reject('PowerPoint get properties failed');
                } else {
                    resolve({
                        Author: properties.author,
                        Category: properties.category,
                        Comments: properties.comments,
                        CreationDate: properties.creationDate?.toISOString(),
                        Keywords: properties.keywords,
                        LastAuthor: properties.lastAuthor,
                        LastPrintDate: '',
                        LastSaveTime: '',
                        NumberOfPages: '',
                        RevisionNumber: properties.revisionNumber?.toString(),
                        Subject: properties.subject,
                        Template: '',
                        Title: properties.title,
                    });
                }
            });
        });
    };

    public getHeaderContent = (): Promise<string> => {
        return new Promise((resolve) => {
            PowerPoint.run(async (context) => {
                const headers: string[] = [];

                // load slides collection so we can iterate through
                const slides = context.presentation.slides;
                slides.load('items');
                await context.sync();

                for (const slide of slides.items) {
                    headers.push(await this.getTextFromShapeByName(slide.slideMaster, this.headerShapeName));
                }

                resolve(headers[0]);
            });
        });
    };

    public getFooterContent = (): Promise<string> => {
        return new Promise((resolve) => {
            PowerPoint.run(async (context) => {
                const footers: string[] = [];

                // load slides collection so we can iterate through
                const slides = context.presentation.slides;
                slides.load('items');
                await context.sync();

                for (const slide of slides.items) {
                    footers.push(await this.getTextFromShapeByName(slide.slideMaster, this.footerShapeName));
                }

                resolve(footers[0]);
            });
        });
    };

    public setHeaderAndFooterAsync = async (
        location: 'header' | 'footer',
        headerFooterText: string,
        options: HeaderFooterOptions = defaultHeaderFooterOptions(),
    ): Promise<void> => {
        return new Promise((resolve) => {
            PowerPoint.run(async (context) => {
                const prefix = options.bookmarkPrefix;
                const headerFooterName = prefix + (location == 'header' ? 'Header' : 'Footer');
                const legacyHeaderFooterName =
                    prefix === 'Titus' ? undefined : 'Titus' + (location == 'header' ? 'Header' : 'Footer');
                const plainHeaderFooterText = this.getPlainTextFromHtml(headerFooterText);
                let slideDimensions: { height: number; width: number } | undefined;
                const processedSlideMasters = [] as string[];
                const targets = options.slidesAndPages;

                // load slides collection so we can iterate through
                const presentation = context.presentation;
                const slides = presentation.slides;
                slides.load('items');
                await context.sync();

                slideDimensions = await this.getSlideDimensionsFromPageSetup(presentation);

                if (targets.includes('All slides')) {
                    for (const slide of slides.items) {
                        const slideMaster = slide.slideMaster;
                        slideMaster.load('id');
                        await slideMaster.context.sync();
                        const slideMasterId = slideMaster.id;
                        if (processedSlideMasters.includes(slideMasterId)) continue;

                        slideDimensions = await this.setTextBoxHeaderFooter(
                            slideMaster,
                            headerFooterName,
                            plainHeaderFooterText,
                            location,
                            slideDimensions,
                            options.horizontalPosition,
                            options.verticalPosition,
                            legacyHeaderFooterName,
                        );
                        processedSlideMasters.push(slideMasterId);
                    }
                }

                const directSlides: PowerPoint.Slide[] = [];
                if (!targets.includes('All slides')) {
                    if (targets.includes('First slide') && slides.items.length > 0) {
                        directSlides.push(slides.items[0]);
                    }
                    if (targets.includes('Title slide')) {
                        for (const slide of slides.items) slide.layout.load('name');
                        await context.sync();
                        directSlides.push(
                            ...slides.items.filter(
                                (slide) => slide.layout.name.toLowerCase() === 'title slide',
                            ),
                        );
                    }
                }

                for (const slide of directSlides.filter((item, index, values) => values.indexOf(item) === index)) {
                    slideDimensions = await this.setTextBoxHeaderFooter(
                        slide,
                        headerFooterName,
                        plainHeaderFooterText,
                        location,
                        slideDimensions,
                        options.horizontalPosition,
                        options.verticalPosition,
                        legacyHeaderFooterName,
                    );
                }

                if (targets.includes('Handout pages') || targets.includes('Note pages')) {
                    console.warn('PowerPoint handout and note page markings are not supported by Office.js.');
                }

                resolve();
            });
        });
    };

    private async setTextBoxHeaderFooter(
        target: PowerPoint.Slide | PowerPoint.SlideMaster | PowerPoint.SlideLayout,
        shapeName: string,
        text: string,
        location: 'header' | 'footer',
        slideDimensions: { height: number; width: number } | undefined,
        horizontalPosition = 0,
        verticalPosition = 0,
        legacyShapeName?: string,
    ): Promise<{ height: number; width: number }> {
        let shape = await this.getShapeByName(target, shapeName);
        const legacyShape = legacyShapeName ? await this.getShapeByName(target, legacyShapeName) : undefined;

        if (!shape && legacyShape) {
            shape = legacyShape;
        } else if (shape && legacyShape && shape !== legacyShape) {
            legacyShape.delete();
            await legacyShape.context.sync();
        }

        if (shape) {
            const currentText = await this.getTextFromShape(shape);
            if (currentText != text) shape.textFrame.textRange.text = text;
        } else {
            shape = target.shapes.addTextBox(text);
        }

        if (!slideDimensions) {
            slideDimensions = await this.getSlideDimensionsFromInsertedShape(shape, target);
        }

        shape.name = shapeName;
        shape.textFrame.verticalAlignment = location === 'header' ? 'Top' : 'Bottom';
        if (shape.textFrame.textRange.paragraphFormat) {
            shape.textFrame.textRange.paragraphFormat.horizontalAlignment = 'Left';
        }
        shape.left = horizontalPosition;
        shape.width = Math.max(0, slideDimensions.width - horizontalPosition);
        shape.top =
            location == 'header'
                ? verticalPosition
                : slideDimensions.height - this.headerFooterShapeHeight - verticalPosition;
        shape.height = this.headerFooterShapeHeight;
        await shape.context.sync();

        return slideDimensions;
    }

    private async getSlideDimensionsFromPageSetup(
        presentation: PowerPoint.Presentation,
    ): Promise<{ height: number; width: number } | undefined> {
        if (!Office.context.requirements.isSetSupported('PowerPointApi', '1.10')) {
            return undefined;
        }

        const pageSetup = presentation.pageSetup;
        pageSetup.load(['slideHeight', 'slideWidth']);
        await pageSetup.context.sync();

        return {
            height: pageSetup.slideHeight,
            width: pageSetup.slideWidth,
        };
    }

    private async getSlideDimensionsFromInsertedShape(
        shape: PowerPoint.Shape,
        target: PowerPoint.Slide | PowerPoint.SlideMaster | PowerPoint.SlideLayout,
    ): Promise<{ height: number; width: number }> {
        await target.context.sync();

        // Older PowerPoint API sets do not expose slide dimensions. The inserted text box starts centered,
        // so its position and size let us calculate the slide bounds before moving it.
        shape.load(['left', 'top', 'width', 'height']);
        await shape.context.sync();

        return {
            height: (shape.top + shape.height / 2) * 2,
            width: (shape.left + shape.width / 2) * 2,
        };
    }

    public getPlatform = (): string => {
        const platformType = this.getPlatformType();
        let platform: string;
        switch (platformType) {
            case 'Android':
                platform = 'PowerPointAndroid';
                break;
            case 'iOS':
            case 'Mac':
                platform = 'PowerPointIOS';
                break;
            case 'OfficeOnline':
                platform = 'PowerPointWebApp';
                break;
            default:
                platform = 'PowerPoint';
                break;
        }
        return platform;
    };

    private async getShapeByName(
        slide: PowerPoint.Slide | PowerPoint.SlideMaster | PowerPoint.SlideLayout,
        shapeName: string,
    ): Promise<PowerPoint.Shape | undefined> {
        try {
            // load the shapes collection so we can iterate through
            slide.load('shapes');
            await slide.context.sync();

            const shapes = slide.shapes;
            shapes.load('items/name');
            await shapes.context.sync();

            for (const shape of shapes.items) {
                if (shape.name == shapeName) {
                    return shape;
                }
            }
        } catch {
            console.log('error loading shapes in slide');
        }

        return undefined;
    }

    private async getTextFromShape(shape: PowerPoint.Shape): Promise<string> {
        try {
            shape.load('textFrame/textRange/text');
            await shape.context.sync();

            return shape.textFrame.textRange.text;
        } catch {
            console.log('Error retrieving text from shape');
        }

        return '';
    }

    private async getTextFromShapeByName(
        slide: PowerPoint.Slide | PowerPoint.SlideMaster | PowerPoint.SlideLayout,
        shapeName: string,
    ): Promise<string> {
        const namedShape = await this.getShapeByName(slide, shapeName);

        return namedShape ? await this.getTextFromShape(namedShape) : '';
    }

    private getPlainTextFromHtml(html: string): string {
        // The source returns an HTML fragment. Wrapping it in a body preserves leading
        // whitespace that document parsing would otherwise discard. Full HTML documents
        // must not be wrapped this way, as head content could become marking text.
        const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');

        // These elements normally occupy separate lines in rendered HTML.
        const blockTags = new Set([
            'DIV', 'P', 'PRE', 'SECTION', 'TABLE', 'TR',
            'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
            'HR', 'LI', 'OL', 'UL' 
        ]);

        let text = '';

        // Separate block elements without adding duplicate line breaks
        // for nested structures such as <div><p>...</p></div>.
        const ensureNewline = (): void => {
            if (text && !text.endsWith('\n')) {
                text += '\n';
            }
        };

        const visit = (node: Node): void => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent ?? '';
                return;
            }

            if (node.nodeType !== Node.ELEMENT_NODE) {
                return;
            }

            const element = node as Element;
            const tag = element.tagName;

            // Exclude content that isn't part of the displayed message.
            if (['SCRIPT', 'STYLE', 'TEMPLATE'].includes(tag)) {
                return;
            }

            // Explicit breaks always add a newline, preserving blank lines
            // created by consecutive <br> elements.
            if (tag === 'BR') {
                text += '\n';
                return;
            }

            const isBlock = blockTags.has(tag);

            // A break before the block also handles inline text followed
            // by a block, such as Hello<div>World</div>.
            if (isBlock) {
                ensureNewline();
            }

            // Separate table cells with tabs; TR adds the row's line break.
            if (
                (tag === 'TD' || tag === 'TH') &&
                element.previousElementSibling
            ) {
                text += '\t';
            }

            element.childNodes.forEach(visit);

            if (isBlock) {
                ensureNewline();
            }
        };

        doc.body.childNodes.forEach(visit);

        // Preserve leading/trailing whitespace and explicit blank lines.
        return text;
    }
}
